//
// # Schematic Connectivity
//
// One answer to "what meets at this point", shared by everything that needs
// it. Before this module there were three different answers:
//
//   - `dots.ts` seeded candidates from wire endpoints, then tested every
//     wire's span, and ignored pins entirely.
//   - `traceWireNetwork` counted wire *endpoints* only, so a wire ending on
//     another wire's span — a T that both dot inference and the circuit
//     extractor treat as a junction — was invisible to it.
//   - The circuit extractor connects an instance pin when a wire *endpoint*
//     falls inside the pin's box (`ConnectedGroup.intersectsBbox`).
//
// The first two are unified here, and agree with the extractor on the rule
// that matters: connections are made by wire endpoints, not by a wire passing
// over something. The extractor still differs in using the pin's box as a
// tolerance where this module demands exact coordinates.
//
// Structural by design: this takes anything with `start()`/`end()` rather than
// a `WireSegment`, so it stays free of the content/schematic tree.
//

import { Point } from "GenAlphaModel";
import { pointKey } from "./wire_utils";

// The slice of a wire segment these tests need.
export interface SegmentLike {
  start(): Point;
  end(): Point;
}

export interface ConnectivityIndex {
  // How many segments have an endpoint exactly at `pt`. Each segment counts
  // once, so a zero-length segment sitting at `pt` contributes 1.
  endsAt: (pt: Point) => number;
  // How many segments contain `pt` strictly inside their span, i.e. pass
  // through without terminating there.
  through: (pt: Point) => number;
  // Does the net branch here — does a wire-following walk have to stop?
  //
  //   3+ segments ending here            -> a `+` or split T
  //   a segment ending here AND another
  //     passing through                  -> a mid-span T
  //
  // Two segments ending here is a corner or butt joint: the walk continues
  // through it. Segments merely crossing (neither terminating) do not connect
  // and so do not branch.
  isBranchPoint: (pt: Point) => boolean;

  // Should a junction dot be drawn here?
  //
  // Wires only. A pin resting on a wire's span is deliberately not a dot: the
  // circuit extractor connects an instance pin by testing whether a wire
  // *endpoint* falls inside the pin's box (`ConnectedGroup.intersectsBbox`),
  // so a wire merely passing over a pin is not connected. Drawing a dot there
  // would assert a connection the netlist does not have.
  isDot: (pt: Point) => boolean;

  // Every point worth evaluating: the wire endpoints. A point interior to a
  // wire with nothing else there can never be a junction, so it is not
  // enumerated.
  candidatePoints: () => Point[];
}

// Is `pt` strictly inside `seg`'s span — on it, but not at either end?
// Zero-length and diagonal segments have no interior.
function containsInterior(seg: SegmentLike, pt: Point): boolean {
  const s = seg.start();
  const e = seg.end();
  if (s.x === e.x) {
    if (s.y === e.y) return false; // zero-length
    if (pt.x !== s.x) return false;
    return pt.y > Math.min(s.y, e.y) && pt.y < Math.max(s.y, e.y);
  }
  if (s.y === e.y) {
    if (pt.y !== s.y) return false;
    return pt.x > Math.min(s.x, e.x) && pt.x < Math.max(s.x, e.x);
  }
  return false; // diagonal: no meaningful span test
}

// Build an index over `segments` and `pinLocations`.
export function buildConnectivity(segments: SegmentLike[]): ConnectivityIndex {
  // Endpoint incidence, and the candidate set.
  const endCounts = new Map<string, number>();
  const candidates = new Map<string, Point>();
  const noteCandidate = (pt: Point) => {
    const key = pointKey(pt);
    if (!candidates.has(key)) candidates.set(key, { x: pt.x, y: pt.y });
  };

  // Bucket axis-aligned segments so a span query scans only the collinear
  // ones rather than every wire in the schematic.
  const byRow = new Map<number, SegmentLike[]>();
  const byCol = new Map<number, SegmentLike[]>();
  const bucket = (
    map: Map<number, SegmentLike[]>,
    key: number,
    seg: SegmentLike
  ) => {
    const existing = map.get(key);
    if (existing) existing.push(seg);
    else map.set(key, [seg]);
  };

  for (const seg of segments) {
    const s = seg.start();
    const e = seg.end();
    noteCandidate(s);
    noteCandidate(e);

    // Count the segment once per distinct endpoint: a zero-length segment
    // contributes a single incidence, not two.
    const sKey = pointKey(s);
    const eKey = pointKey(e);
    endCounts.set(sKey, (endCounts.get(sKey) ?? 0) + 1);
    if (eKey !== sKey) endCounts.set(eKey, (endCounts.get(eKey) ?? 0) + 1);

    if (s.x === e.x && s.y !== e.y) bucket(byCol, s.x, seg);
    else if (s.y === e.y && s.x !== e.x) bucket(byRow, s.y, seg);
    // Zero-length and diagonal segments have no interior to index.
  }

  const endsAt = (pt: Point) => endCounts.get(pointKey(pt)) ?? 0;

  const through = (pt: Point) => {
    let count = 0;
    for (const seg of byRow.get(pt.y) ?? []) {
      if (containsInterior(seg, pt)) count++;
    }
    for (const seg of byCol.get(pt.x) ?? []) {
      if (containsInterior(seg, pt)) count++;
    }
    return count;
  };

  const isBranchPoint = (pt: Point) => {
    const ends = endsAt(pt);
    if (ends >= 3) return true;
    return ends >= 1 && through(pt) >= 1;
  };

  const isDot = (pt: Point) => {
    const ends = endsAt(pt);
    if (ends >= 3) return true;
    return through(pt) >= 1 && ends >= 1;
  };

  return {
    endsAt,
    through,
    isBranchPoint,
    isDot,
    candidatePoints: () => Array.from(candidates.values()),
  };
}

//
// # Net Membership
//
// Which pins share a net. `checkWireInvariants` only sees geometry — overlaps,
// diagonals, zero-length segments — and a net coming quietly apart violates
// none of those. Every drag regression found by hand so far has been of that
// kind: the geometry was perfectly legal and the circuit was wrong.
//
// Mirrors the circuit extractor's rules: wires join where an endpoint of one
// lands anywhere on the other, and a pin joins a wire that ends on it. (The
// extractor allows a pin's box as tolerance where this demands exact
// coordinates; see the note above.)
//

export interface NetPin {
  // Stable across a move — the pin's identity, not its position.
  id: string;
  loc: Point;
}

// Is `pt` anywhere on `seg`, endpoints included?
function touches(seg: SegmentLike, pt: Point): boolean {
  const s = seg.start();
  const e = seg.end();
  if (Point.eq(s, pt) || Point.eq(e, pt)) return true;
  return containsInterior(seg, pt);
}

// Do two segments connect?
//
// The definition `netsOf` partitions by, but not how it computes it: testing
// every pair is quadratic. Kept as the reference `nets_of.test.ts` checks the
// fast path against.
export function segmentsConnect(a: SegmentLike, b: SegmentLike): boolean {
  return (
    touches(b, a.start()) ||
    touches(b, a.end()) ||
    touches(a, b.start()) ||
    touches(a, b.end())
  );
}

// A maximal span of collinear segments that overlap or abut, so are all one
// net. `slot` is any one of the points they were built from.
interface Run {
  lo: number;
  hi: number;
  slot: number;
}

// The run covering `at`, or null. Runs are disjoint and sorted by `lo`, so the
// only candidate is the rightmost one starting at or before `at`.
function stab(runs: Run[], at: number): Run | null {
  let lo = 0;
  let hi = runs.length - 1;
  let best: Run | null = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (runs[mid].lo <= at) {
      best = runs[mid];
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best && at <= best.hi ? best : null;
}

// Partition `pins` into nets, as sorted groups of pin ids. A pin on no wire
// forms a net of its own, so losing a connection shows up as a group splitting
// rather than a pin vanishing.
//
// Segments only connect where an endpoint of one lies on the other, so the 2N
// endpoints are the only places a connection can be made — union-find runs
// over *points*, not pairs of segments. A wire joins its own two ends; each
// endpoint also joins whatever its row and column say it landed on.
export function netsOf(wires: SegmentLike[], pins: NetPin[]): string[][] {
  // Pin locations deliberately get no slot: a pin on no wire must stay its own
  // net, and two such pins in the same spot are two nets, not one.
  const slotOf = new Map<string, number>();
  const slotPt: Point[] = [];
  const slotFor = (p: Point): number => {
    const key = pointKey(p);
    const found = slotOf.get(key);
    if (found !== undefined) return found;
    const idx = slotPt.length;
    slotPt.push(p);
    slotOf.set(key, idx);
    return idx;
  };

  const startSlot: number[] = [];
  const endSlot: number[] = [];
  // Diagonal and zero-length segments have no interior to land in, so they
  // connect only by exact endpoint coincidence and stay out of the buckets.
  const horizByY = new Map<number, number[]>();
  const vertByX = new Map<number, number[]>();
  const bucket = (m: Map<number, number[]>, key: number, i: number) => {
    const at = m.get(key);
    if (at) at.push(i);
    else m.set(key, [i]);
  };

  for (let i = 0; i < wires.length; i++) {
    const a = wires[i].start();
    const b = wires[i].end();
    startSlot.push(slotFor(a));
    endSlot.push(slotFor(b));
    if (a.y === b.y && a.x !== b.x) bucket(horizByY, a.y, i);
    else if (a.x === b.x && a.y !== b.y) bucket(vertByX, a.x, i);
  }

  const parent = slotPt.map((_, i) => i);
  const find = (i: number): number => {
    let root = i;
    while (parent[root] !== root) root = parent[root];
    while (parent[i] !== root) {
      const next = parent[i];
      parent[i] = root;
      i = next;
    }
    return root;
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  // A wire makes its own two ends common.
  for (let i = 0; i < wires.length; i++) union(startSlot[i], endSlot[i]);

  // Sweep each bucket into runs, unioning as collinear neighbours merge, so a
  // query binary-searches instead of scanning a bucket holding a whole bus.
  // `along` picks the coordinate the bucket varies in.
  const buildRuns = (
    byKey: Map<number, number[]>,
    along: (p: Point) => number
  ): Map<number, Run[]> => {
    const out = new Map<number, Run[]>();
    for (const [key, idxs] of byKey) {
      const spans = idxs.map((i) => {
        const u = along(wires[i].start());
        const v = along(wires[i].end());
        return { lo: Math.min(u, v), hi: Math.max(u, v), slot: startSlot[i] };
      });
      spans.sort((p, q) => p.lo - q.lo);
      const runs: Run[] = [];
      for (const span of spans) {
        const cur = runs.length === 0 ? null : runs[runs.length - 1];
        // `<=`, not `<`: collinear segments that merely abut are connected,
        // the endpoint of one lying on the other.
        if (cur && span.lo <= cur.hi) {
          union(cur.slot, span.slot);
          if (span.hi > cur.hi) cur.hi = span.hi;
        } else {
          runs.push({ lo: span.lo, hi: span.hi, slot: span.slot });
        }
      }
      out.set(key, runs);
    }
    return out;
  };
  const runsByY = buildRuns(horizByY, (p) => p.x);
  const runsByX = buildRuns(vertByX, (p) => p.y);

  // A run covers its span without gaps, so a point inside one is on a member.
  for (let slot = 0; slot < slotPt.length; slot++) {
    const p = slotPt[slot];
    const row = runsByY.get(p.y);
    if (row) {
      const hit = stab(row, p.x);
      if (hit) union(slot, hit.slot);
    }
    const col = runsByX.get(p.x);
    if (col) {
      const hit = stab(col, p.y);
      if (hit) union(slot, hit.slot);
    }
  }

  const byRoot = new Map<number, string[]>();
  const unattached: string[][] = [];
  for (const pin of pins) {
    const slot = slotOf.get(pointKey(pin.loc));
    if (slot === undefined) {
      unattached.push([pin.id]);
      continue;
    }
    const root = find(slot);
    const group = byRoot.get(root);
    if (group) group.push(pin.id);
    else byRoot.set(root, [pin.id]);
  }

  return [...byRoot.values(), ...unattached]
    .map((group) => group.slice().sort())
    .sort((a, b) => (a.join() < b.join() ? -1 : 1));
}

// Describe how net membership changed, or null if it did not. Pins are matched
// by id, so a move that carries a pin along is not itself a change.
export function describeNetChanges(
  before: string[][],
  after: string[][]
): string | null {
  const render = (nets: string[][]) =>
    nets.map((net) => net.join("+")).join("  ");
  const a = render(before);
  const b = render(after);
  return a === b ? null : `nets before: ${a}\nnets after:  ${b}`;
}
